/** UI state shared across the app. Persistent data lives in IndexedDB. */
import { create } from 'zustand';
import type { CapabilityProbe, Benchmark, DeviceTier } from '../lib/capability';
import type { PipelineSettings } from '../features/pipeline/settings';

export type CompareMode = 'slider' | 'side' | 'toggle' | 'loupe';

export interface ImageProgress {
  label: string;
  /** 0..1, or null when indeterminate. */
  fraction: number | null;
  etaMs: number | null;
}

export interface DeviceState {
  probe: CapabilityProbe;
  benchmark: Benchmark | null;
  tier: DeviceTier;
}

/** Counts for the batch currently being worked through (persisted, see runner). */
export interface BatchState {
  total: number;
  done: number;
  failed: number;
}

/** A quick low-resolution run of the selected photo, held in memory only. */
export interface PreviewState {
  imageId: string;
  url: string;
  width: number;
  height: number;
  /** stableStringify of the settings it was made with, to flag out-of-date previews. */
  settingsKey: string;
}

interface AppState {
  albumId: string | null;
  selectedId: string | null;
  selection: string[];
  compareMode: CompareMode;
  sliderVertical: boolean;
  progress: Record<string, ImageProgress>;
  device: DeviceState | null;
  queueRunning: boolean;
  queuePaused: boolean;
  batch: BatchState | null;
  /** Measured restoration speed in input megapixels per second, or null before the first tiles. */
  mpPerSec: number | null;
  keepAwake: boolean;
  preview: PreviewState | null;
  previewBusy: string | null;
  /** Settings edits shown immediately while they are being saved (by image id). */
  drafts: Record<string, PipelineSettings>;

  setAlbum(id: string | null): void;
  select(id: string | null): void;
  setSelection(ids: string[]): void;
  setCompareMode(mode: CompareMode): void;
  toggleSliderOrientation(): void;
  setProgress(id: string, p: ImageProgress | null): void;
  setDevice(d: DeviceState): void;
  setQueueRunning(running: boolean): void;
  setQueuePaused(paused: boolean): void;
  setBatch(b: BatchState | null): void;
  setMpPerSec(v: number | null): void;
  setKeepAwake(v: boolean): void;
  setPreview(p: PreviewState | null): void;
  setPreviewBusy(label: string | null): void;
  setDraft(id: string, s: PipelineSettings | null): void;
}

export const useApp = create<AppState>((set) => ({
  albumId: null,
  selectedId: null,
  selection: [],
  compareMode: 'slider',
  sliderVertical: true,
  progress: {},
  device: null,
  queueRunning: false,
  queuePaused: false,
  batch: null,
  mpPerSec: null,
  keepAwake: true,
  preview: null,
  previewBusy: null,
  drafts: {},

  setAlbum: (albumId) => set({ albumId, selectedId: null, selection: [] }),
  select: (selectedId) => set({ selectedId }),
  setSelection: (selection) => set({ selection }),
  setCompareMode: (compareMode) => set({ compareMode }),
  toggleSliderOrientation: () => set((s) => ({ sliderVertical: !s.sliderVertical })),
  setProgress: (id, p) =>
    set((s) => {
      const progress = { ...s.progress };
      if (p) progress[id] = p;
      else delete progress[id];
      return { progress };
    }),
  setDevice: (device) => set({ device }),
  setQueueRunning: (queueRunning) => set({ queueRunning }),
  setQueuePaused: (queuePaused) => set({ queuePaused }),
  setBatch: (batch) => set({ batch }),
  setMpPerSec: (mpPerSec) => set({ mpPerSec }),
  setKeepAwake: (keepAwake) => set({ keepAwake }),
  setPreview: (preview) =>
    set((s) => {
      if (s.preview && s.preview.url !== preview?.url) URL.revokeObjectURL(s.preview.url);
      return { preview };
    }),
  setPreviewBusy: (previewBusy) => set({ previewBusy }),
  setDraft: (id, d) =>
    set((s) => {
      const drafts = { ...s.drafts };
      if (d) drafts[id] = d;
      else delete drafts[id];
      return { drafts };
    }),
}));
